// ABOUTME: Pure helpers for invite codes: normalization, generation, status, and admin validation.
// ABOUTME: No Firebase here; the rules in firestore.rules mirror isInviteRedeemable.

/** An invite code as stored at invites/{code}. The code is the document id. */
export interface InviteRecord {
  code: string;
  /** Who it's for — admin-only, never shown to users. */
  label: string;
  /** 0 means unlimited. */
  maxUses: number;
  uses: number;
  /** Paused invites are kept on file but cannot be redeemed. */
  active: boolean;
  /** Epoch ms, or null for no expiry. */
  expiresAt: number | null;
  createdAt: number | null;
  updatedAt: number | null;
}

export type InviteInput = Pick<InviteRecord, "code" | "label" | "maxUses" | "active" | "expiresAt">;

export const INVITE_LIMITS = {
  codeMin: 4,
  codeMax: 24,
  labelMax: 80,
  maxUsesMax: 1000,
} as const;

export const EMPTY_INVITE_INPUT: InviteInput = {
  code: "",
  label: "",
  maxUses: 5,
  active: true,
  expiresAt: null,
};

/** Codes are case-insensitive and ignore spaces, so "brz 7k3q" and "BRZ-7K3Q" are the same thing. */
export function normalizeInviteCode(raw: string): string {
  return raw.trim().toUpperCase().replace(/\s+/g, "");
}

const CODE_PATTERN = /^[A-Z0-9][A-Z0-9-]*[A-Z0-9]$/;

export function isWellFormedCode(code: string): boolean {
  return (
    code.length >= INVITE_LIMITS.codeMin &&
    code.length <= INVITE_LIMITS.codeMax &&
    CODE_PATTERN.test(code)
  );
}

// No 0/O or 1/I/L: these get read out loud and typed from screenshots.
const ALPHABET = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";

/** A fresh code like BRZ-7K3QM9. `random` is injectable for tests. */
export function generateInviteCode(random: () => number = Math.random): string {
  let suffix = "";
  for (let i = 0; i < 6; i++) {
    suffix += ALPHABET[Math.floor(random() * ALPHABET.length)];
  }
  return `BRZ-${suffix}`;
}

/** Coerce a Firestore document into one record; tolerant of missing fields. */
export function normalizeInviteRecord(code: string, data: Record<string, unknown>): InviteRecord {
  const num = (v: unknown, fallback: number) =>
    typeof v === "number" && Number.isFinite(v) ? v : fallback;
  const millis = (v: unknown): number | null => {
    if (typeof v === "number" && Number.isFinite(v)) return v;
    if (v && typeof (v as { toMillis?: unknown }).toMillis === "function") {
      return (v as { toMillis: () => number }).toMillis();
    }
    return null;
  };
  return {
    code,
    label: typeof data.label === "string" ? data.label : "",
    maxUses: Math.max(0, Math.floor(num(data.maxUses, 0))),
    uses: Math.max(0, Math.floor(num(data.uses, 0))),
    active: data.active !== false,
    expiresAt: millis(data.expiresAt),
    createdAt: millis(data.createdAt),
    updatedAt: millis(data.updatedAt),
  };
}

export type InviteStatus = "live" | "paused" | "exhausted" | "expired";

/** Why a code can or cannot be redeemed right now. Mirrors `isInviteRedeemable` in firestore.rules. */
export function inviteStatus(invite: InviteRecord, now: number = Date.now()): InviteStatus {
  if (!invite.active) return "paused";
  if (invite.expiresAt !== null && now >= invite.expiresAt) return "expired";
  if (invite.maxUses > 0 && invite.uses >= invite.maxUses) return "exhausted";
  return "live";
}

export function isInviteRedeemable(invite: InviteRecord, now: number = Date.now()): boolean {
  return inviteStatus(invite, now) === "live";
}

/** What the sign-up form tells the user for each non-live status, or an unknown code. */
export function describeInviteProblem(status: InviteStatus | "unknown"): string {
  switch (status) {
    case "unknown":
      return "That invite code isn't one we recognize. Check for typos, or ask whoever invited you.";
    case "paused":
      return "That invite code has been turned off. Ask whoever invited you for a new one.";
    case "exhausted":
      return "That invite code has already been used as many times as it allows.";
    case "expired":
      return "That invite code has expired.";
    default:
      return "That invite code can't be used right now.";
  }
}

export type InviteFieldErrors = Partial<Record<keyof InviteInput, string>>;

/** Validate an admin's edits. Empty map means clean. Mirrors `isValidInvite` in firestore.rules. */
export function validateInviteInput(input: InviteInput): InviteFieldErrors {
  const errors: InviteFieldErrors = {};
  const code = normalizeInviteCode(input.code);
  if (!isWellFormedCode(code)) {
    errors.code = `Use ${INVITE_LIMITS.codeMin}–${INVITE_LIMITS.codeMax} letters, numbers or dashes.`;
  }
  if (input.label.length > INVITE_LIMITS.labelMax) {
    errors.label = `Keep the label under ${INVITE_LIMITS.labelMax} characters.`;
  }
  if (
    !Number.isInteger(input.maxUses) ||
    input.maxUses < 0 ||
    input.maxUses > INVITE_LIMITS.maxUsesMax
  ) {
    errors.maxUses = `Max uses must be a whole number from 0 (unlimited) to ${INVITE_LIMITS.maxUsesMax}.`;
  }
  if (input.expiresAt !== null && !Number.isFinite(input.expiresAt)) {
    errors.expiresAt = "That isn't a valid date.";
  }
  return errors;
}

/** Trim and normalize edits into exactly what gets written. */
export function prepareInviteInput(input: InviteInput): InviteInput {
  return {
    code: normalizeInviteCode(input.code),
    label: input.label.trim(),
    maxUses: Math.max(0, Math.floor(input.maxUses)),
    active: input.active,
    expiresAt: input.expiresAt,
  };
}

/** The link to hand a friend: opens the sign-up form with the code filled in. */
export function inviteLink(code: string, origin: string): string {
  return `${origin.replace(/\/$/, "")}/?invite=${encodeURIComponent(code)}`;
}
